import { Button } from "@/components/ui/button";
import { forwardRef, useCallback } from "react";
import { trackConversion } from "@/lib/conversionTracking";

const TIDYCAL_URL =
  "https://tidycal.com/team/infinity-financial-solutions/accounting-discovery";

function logBookingClick(page: string, placement: string) {
  let heroVariant: string | null = null;
  try {
    heroVariant = sessionStorage.getItem("hero_variant");
  } catch {}

  const payload = JSON.stringify({
    page,
    referrer: document.referrer || null,
    hero_variant: heroVariant,
    placement,
  });

  const url = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/log-booking-click`;

  fetch(url, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: payload,
    keepalive: true,
  }).catch(() => {});

  if (window.dataLayer) {
    window.dataLayer.push([
      "event",
      "begin_checkout",
      { event_category: "booking", event_label: page, placement },
    ]);
  }
}

interface TrackedBookingLinkProps {
  children: React.ReactNode;
  className?: string;
  variant?: "default" | "outline" | "ghost" | "link" | "destructive" | "secondary";
  size?: "default" | "sm" | "lg" | "icon";
  /** Override the default TidyCal URL */
  href?: string;
  /** Additional onClick handler (runs alongside tracking) */
  onClick?: () => void;
  /** Placement label for analytics breakdown (e.g. 'hero', 'mobile_sticky', 'footer_cta') */
  placement?: string;
}

/**
 * A booking CTA that tracks the click reliably before opening TidyCal.
 * Uses onMouseDown to guarantee the tracking fires before any browser
 * interception of the anchor navigation.
 */
export const TrackedBookingLink = forwardRef<
  HTMLAnchorElement,
  TrackedBookingLinkProps
>(({ children, className, variant = "default", size = "lg", href, onClick: extraOnClick, placement = "inline" }, ref) => {
  const handleMouseDown = useCallback(
    (e: React.MouseEvent<HTMLAnchorElement>) => {
      if (e.button !== 0) return;
      logBookingClick(window.location.pathname, placement);
      trackConversion("booking_click", { sourceLabel: placement });
      extraOnClick?.();
    },
    [extraOnClick, placement]
  );

  return (
    <a
      ref={ref}
      href={href || TIDYCAL_URL}
      target="_blank"
      rel="noopener noreferrer"
      onMouseDown={handleMouseDown}
      className="inline-flex"
    >
      <Button variant={variant} size={size} className={className} tabIndex={-1} type="button">
        {children}
      </Button>
    </a>
  );
});

TrackedBookingLink.displayName = "TrackedBookingLink";
